import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../utils/api';
import { Link as RouterLink } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import AssessmentIcon from '@mui/icons-material/Assessment';
import DeleteIcon from '@mui/icons-material/Delete';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

const SessionList = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/admin/sessions');
      setSessions(response.data);
    } catch (err) {
      console.error("Error fetching sessions:", err);
      setError(err.response?.data?.message || 'Failed to load sessions.');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const handleDeleteClick = (sessionId) => {
    setSessionToDelete(sessionId);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSessionToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!sessionToDelete) return;
    try {
      await api.delete(`/sessions/${sessionToDelete}`);
      setSuccessMessage('Session deleted successfully.');
      fetchSessions();
    } catch (err) {
      console.error(`Error deleting session ${sessionToDelete}:`, err);
      setError(err.response?.data?.message || 'Failed to delete session.');
    }
    handleCloseDialog();
  };

  const handleCloseSnackbar = () => {
    setSuccessMessage(null);
    setError(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper elevation={3} sx={{ mt: 4, p: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
        Session Management
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table sx={{ minWidth: 650 }} aria-label="session list table">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Deck Type</TableCell>
              <TableCell>Facilitator</TableCell>
              <TableCell align="center">Participants</TableCell>
              <TableCell align="center">Active Items</TableCell>
              <TableCell align="center">Completed Items</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sessions.map((session) => (
              <TableRow key={session.id}>
                <TableCell component="th" scope="row">
                  {session.name}
                </TableCell>
                <TableCell>{session.status}</TableCell>
                <TableCell>{session.cardDeckType}</TableCell>
                <TableCell>{session.facilitatorName || 'N/A'}</TableCell>
                <TableCell align="center">{session.participantCount || 0}</TableCell>
                <TableCell align="center">{session.activeItemCount || 0}</TableCell>
                <TableCell align="center">{session.completedItemCount || 0}</TableCell>
                <TableCell>{new Date(session.createdAt).toLocaleDateString()}</TableCell>
                <TableCell align="center">
                  <IconButton 
                    aria-label="view reflections"
                    component={RouterLink} 
                    to={`/session/${session.sessionId}/reflections`} 
                    size="small"
                    sx={{ mr: 0.5 }}
                  >
                    <AssessmentIcon />
                  </IconButton>
                  <IconButton 
                    aria-label="delete session" 
                    color="error" 
                    size="small"
                    onClick={() => handleDeleteClick(session.sessionId)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {sessions.length === 0 && !loading && (
        <Typography sx={{ textAlign: 'center', mt: 2 }}>No sessions found.</Typography>
      )}
      <Snackbar 
        open={!!successMessage || !!error} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={successMessage ? "success" : "error"} sx={{ width: '100%' }}>
          {successMessage || error}
        </Alert>
      </Snackbar>
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Confirm Delete"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this session? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default SessionList; 